/**
 * UserInsightsTrends — editorial restyle of the User Analytics 30/60/90d
 * trend chart.
 *
 * Phase-5b: matches the editorial pattern shipped on the Section 04
 * ("People needing a hand") block — same tone-accent palette, italic-mixed
 * headline, dropcap numerals on summary cards. Recharts is preserved (the
 * multi-axis tuning is heavy) but rethemed onto the identity-pillar palette.
 *
 * Layout owns its own card chrome (rounded-3xl + tone shadow) so the parent
 * (UserInsights.tsx) does NOT need to wrap it again.
 */

import { useMemo } from 'react'
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts'
import type { TooltipProps } from 'recharts'
import type { ValueType, NameType, Payload } from 'recharts/types/component/DefaultTooltipContent'
import { format } from 'date-fns'
import {
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  MinusIcon,
  ChartBarIcon,
} from '@heroicons/react/24/outline'
import type { UserInsightsTrendsResult, MetricTrendResult } from '@/types'
import { getThemeColors, toRgb } from '@/lib/themeColors'
import { useTheme } from '@/context/ThemeContext'

interface UserInsightsTrendsProps {
  trends: UserInsightsTrendsResult | null
  isLoading: boolean
  days: number
  onDaysChange: (days: number) => void
}

const DAY_OPTIONS = [30, 60, 90] as const

// Editorial chart palette. Brand-tuned hex literals so the chart stays on the
// identity-tone story even when the rest of the surface theme swaps.
const CHART_PALETTE = {
  // MFA adoption — soft green (positive, healthy)
  mfaAdoption: '#76D69D',
  // Phish-resistant — tone-accent deep (warm sand) — the pillar's hero metric
  phishResistantMfa: '#C9AD80',
  // License waste — soft red (semantic for "going up = bad")
  licenseWaste: '#E57373',
  // Total users — horizon blue (neutral count)
  totalUsers: '#7BA8C4',
} as const

type CustomTooltipProps = TooltipProps<ValueType, NameType>

/** Editorial tooltip — uses the section's tone-accent rule and tabular nums. */
const CustomTooltip = ({ active, payload, label }: CustomTooltipProps) => {
  if (!active || !payload || !payload.length) return null
  return (
    <div className="rounded-2xl border border-dark-600 bg-dark-800 px-4 py-3 shadow-[0_24px_60px_-10px_rgba(33,41,56,0.18)]">
      <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500">
        {label}
      </p>
      <div className="mt-2 flex flex-col gap-1.5">
        {payload.map((entry: Payload<ValueType, NameType>, index: number) => {
          const isPercentage =
            entry.dataKey === 'mfaAdoption' || entry.dataKey === 'phishResistantMfa'
          return (
            <div key={index} className="flex items-center gap-2 text-[12px]">
              <div
                className="h-2.5 w-2.5 rounded-sm"
                style={{ backgroundColor: entry.color }}
              />
              <span className="text-slate-400">{entry.name}</span>
              <span className="ml-auto font-display tabular-nums font-semibold text-slate-100 dark:text-slate-100">
                {isPercentage
                  ? `${Number(entry.value).toFixed(1)}%`
                  : Number(entry.value).toLocaleString()}
              </span>
            </div>
          )
        })}
      </div>
    </div>
  )
}

interface LegendPayloadEntry {
  value: string
  color: string
  type?: string
  id?: string
}

const CustomLegend = ({ payload }: { payload?: LegendPayloadEntry[] }) => {
  if (!payload) return null
  return (
    <div className="mt-4 flex flex-wrap justify-center gap-x-5 gap-y-2 border-t border-dark-700 pt-4">
      {payload.map((entry, index) => (
        <div key={index} className="flex items-center gap-2">
          <span
            aria-hidden
            className="h-2.5 w-2.5 rounded-[3px]"
            style={{ background: entry.color }}
          />
          <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-400">
            {entry.value}
          </span>
        </div>
      ))}
    </div>
  )
}

/** Severity color routing for trend direction. Positive = soft green, negative
 *  = soft red, flat = mute slate. Pinned hex literals so the chart's editorial
 *  story stays consistent across theme swaps. */
function trendVisuals(
  direction: 'up' | 'down' | 'flat',
  isFavorable: boolean,
): { fg: string; bg: string; border: string } {
  if (direction === 'flat') {
    return {
      fg: 'rgb(148, 163, 184)',
      bg: 'rgba(148, 163, 184, 0.10)',
      border: 'rgba(148, 163, 184, 0.30)',
    }
  }
  if (isFavorable) {
    return {
      fg: '#4A9B6E',
      bg: 'rgba(74, 155, 110, 0.12)',
      border: 'rgba(74, 155, 110, 0.30)',
    }
  }
  return {
    fg: '#E57373',
    bg: 'rgba(229, 115, 115, 0.12)',
    border: 'rgba(229, 115, 115, 0.30)',
  }
}

/** Editorial summary card — italic dropcap numeral + uppercase label.
 *  Mirrors the KpiStrip/PeopleNeedingHand visual language. */
function TrendSummaryCard({
  label,
  metric,
  isPercentage,
  invertColors,
}: {
  label: string
  metric: MetricTrendResult
  isPercentage: boolean
  /** When true, "up" is bad (e.g., license waste going up is negative). */
  invertColors?: boolean
}) {
  const isFavorable = invertColors
    ? metric.direction === 'down'
    : metric.direction === 'up'

  const visuals = trendVisuals(metric.direction, isFavorable)

  const DirectionIcon =
    metric.direction === 'up'
      ? ArrowTrendingUpIcon
      : metric.direction === 'down'
      ? ArrowTrendingDownIcon
      : MinusIcon

  const formattedValue = isPercentage
    ? `${metric.currentValue.toFixed(1)}%`
    : metric.currentValue.toLocaleString()

  const formattedChange =
    metric.changeAmount !== null
      ? isPercentage
        ? `${Math.abs(metric.changeAmount).toFixed(1)}pp`
        : Math.abs(metric.changeAmount).toLocaleString()
      : null

  return (
    <div
      className="rounded-2xl border bg-dark-900 px-5 py-4"
      style={{ borderColor: visuals.border, background: visuals.bg }}
    >
      <div className="flex items-start justify-between gap-2">
        <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400">
          {label}
        </span>
        <DirectionIcon className="h-4 w-4" style={{ color: visuals.fg }} />
      </div>
      <div
        className="mt-2 font-display font-extrabold italic tabular-nums leading-none"
        style={{
          color: visuals.fg,
          fontSize: 'clamp(28px, 3vw, 36px)',
          letterSpacing: '-0.03em',
        }}
      >
        {formattedValue}
      </div>
      {formattedChange !== null && metric.direction !== 'flat' ? (
        <div
          className="mt-2 text-[10px] font-bold uppercase tracking-[0.14em]"
          style={{ color: visuals.fg }}
        >
          {metric.direction === 'up' ? '▲' : '▼'} {formattedChange} · prior period
        </div>
      ) : (
        <div className="mt-2 text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500">
          No change
        </div>
      )}
    </div>
  )
}

export default function UserInsightsTrends({
  trends,
  isLoading,
  days,
  onDaysChange,
}: UserInsightsTrendsProps) {
  // Subscribe to theme so chart re-renders on toggle.
  useTheme()
  const tc = getThemeColors()

  // Tone-accent grid line — slightly brighter than the surrounding rule so the
  // dashed horizontal grid reads through the ink without dominating.
  const toneSoft = toRgb('--color-secondary-sand-300', 0.15)

  // Transform history data for the chart
  const chartData = useMemo(() => {
    if (!trends?.history || !Array.isArray(trends.history)) return []
    return trends.history.map((point) => ({
      date: format(new Date(point.date), 'MMM d'),
      rawDate: point.date,
      mfaAdoption: point.mfaMemberAdoptionPercentage,
      phishResistantMfa: point.phishResistantMfaPercentage,
      licenseWaste: point.totalLicenseWaste,
      totalUsers: point.totalUsers,
    }))
  }, [trends?.history])

  const hasEnoughData = chartData.length >= 2

  if (isLoading) {
    return (
      <section
        data-tone="identity"
        className="rounded-3xl border border-dark-600 bg-dark-800 p-7 shadow-[0_24px_60px_-10px_rgba(33,41,56,0.12)]"
      >
        <div className="animate-pulse space-y-6">
          <div className="h-3 w-1/4 rounded-full bg-dark-700" />
          <div className="h-7 w-2/3 rounded-lg bg-dark-700" />
          <div className="h-72 rounded-2xl bg-dark-700" />
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-24 rounded-2xl bg-dark-700" />
            ))}
          </div>
        </div>
      </section>
    )
  }

  return (
    <section
      data-tone="identity"
      className="rounded-3xl border border-dark-600 bg-dark-800 p-7 shadow-[0_24px_60px_-10px_rgba(33,41,56,0.12)]"
    >
      {/* ------------------------------------------------------------------ */}
      {/* Editorial header                                                    */}
      {/* ------------------------------------------------------------------ */}
      <header className="mb-5 flex flex-wrap items-end justify-between gap-3 border-b border-dark-700 pb-4">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-slate-500">
            Section · 03 · {days}-day trend
          </div>
          <h3 className="mt-1 text-[22px] font-extrabold tracking-[-0.015em] text-slate-100 dark:text-slate-100">
            Adoption,{' '}
            <em
              style={{
                fontStyle: 'italic',
                fontWeight: 600,
                color: 'var(--tone-accent-deep)',
              }}
            >
              over the past {days === 30 ? 'month' : days === 60 ? 'two months' : 'quarter'}
            </em>
            .
          </h3>
          <p className="mt-1.5 text-[12px] italic leading-[1.55] text-slate-400">
            Daily MFA adoption, phish-resistant share, license waste, and headcount.
          </p>
        </div>

        <div className="inline-flex items-center gap-1 rounded-full border border-dark-600 bg-dark-900 p-1">
          {DAY_OPTIONS.map((option) => {
            const isActive = days === option
            return (
              <button
                key={option}
                onClick={() => onDaysChange(option)}
                className={`inline-flex items-center rounded-full px-4 py-1.5 text-[10px] font-bold uppercase tracking-[0.18em] transition-all duration-200 ${
                  isActive
                    ? 'bg-[#212938] text-white shadow-[0_4px_12px_rgba(33,41,56,0.18)]'
                    : 'text-slate-500 hover:text-slate-100'
                }`}
              >
                {option}d
              </button>
            )
          })}
        </div>
      </header>

      {/* ------------------------------------------------------------------ */}
      {/* Chart                                                               */}
      {/* ------------------------------------------------------------------ */}
      {!hasEnoughData ? (
        <div className="grid h-72 place-items-center rounded-2xl border border-dashed border-dark-600 bg-dark-900">
          <div className="text-center">
            <ChartBarIcon className="mx-auto h-10 w-10 text-slate-600 mb-3" />
            <p className="font-display text-[18px] font-extrabold tracking-[-0.015em] text-slate-200">
              Collecting{' '}
              <em
                style={{
                  fontStyle: 'italic',
                  fontWeight: 600,
                  color: 'var(--tone-accent-deep)',
                }}
              >
                signal
              </em>
              .
            </p>
            <p className="mt-2 max-w-md text-[12px] text-slate-500">
              A trend needs at least two scans on file. Run another scan and the line
              will appear here.
            </p>
          </div>
        </div>
      ) : (
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData} margin={{ top: 8, right: 8, bottom: 0, left: -8 }}>
              <CartesianGrid
                strokeDasharray="2 4"
                stroke={toneSoft}
                vertical={false}
              />
              <XAxis
                dataKey="date"
                tick={{ fontSize: 10, fill: tc.chartAxisLabel, fontWeight: 600 }}
                tickLine={false}
                axisLine={{ stroke: tc.chartAxis }}
              />
              {/* Left Y-axis: percentages */}
              <YAxis
                yAxisId="percentage"
                domain={[0, 100]}
                tick={{ fontSize: 10, fill: tc.chartAxisLabel, fontWeight: 600 }}
                tickLine={false}
                axisLine={false}
                tickFormatter={(value) => `${value}%`}
              />
              {/* Right Y-axis: counts */}
              <YAxis
                yAxisId="count"
                orientation="right"
                tick={{ fontSize: 10, fill: tc.chartAxisLabel, fontWeight: 600 }}
                tickLine={false}
                axisLine={false}
                tickFormatter={(value) =>
                  value >= 1000 ? `${(value / 1000).toFixed(1)}k` : String(value)
                }
              />
              <Tooltip
                content={<CustomTooltip />}
                cursor={{ stroke: 'var(--tone-accent-deep)', strokeWidth: 1, strokeDasharray: '3 3' }}
              />
              <Legend content={<CustomLegend />} />

              {/* Percentage lines (left axis) */}
              <Line
                yAxisId="percentage"
                type="monotone"
                dataKey="mfaAdoption"
                stroke={CHART_PALETTE.mfaAdoption}
                strokeWidth={2.5}
                name="MFA Adoption %"
                dot={{ fill: CHART_PALETTE.mfaAdoption, strokeWidth: 0, r: 3 }}
                activeDot={{
                  r: 6,
                  stroke: CHART_PALETTE.mfaAdoption,
                  strokeWidth: 2,
                  fill: tc.chartActiveDotFill,
                }}
              />
              <Line
                yAxisId="percentage"
                type="monotone"
                dataKey="phishResistantMfa"
                stroke={CHART_PALETTE.phishResistantMfa}
                strokeWidth={3}
                name="Phish-Resistant MFA %"
                dot={{ fill: CHART_PALETTE.phishResistantMfa, strokeWidth: 0, r: 4 }}
                activeDot={{
                  r: 7,
                  stroke: CHART_PALETTE.phishResistantMfa,
                  strokeWidth: 2,
                  fill: tc.chartActiveDotFill,
                }}
              />

              {/* Count lines (right axis) — dashed to differentiate from percent lines */}
              <Line
                yAxisId="count"
                type="monotone"
                dataKey="licenseWaste"
                stroke={CHART_PALETTE.licenseWaste}
                strokeWidth={1.75}
                strokeDasharray="6 4"
                name="License Waste"
                dot={{ fill: CHART_PALETTE.licenseWaste, strokeWidth: 0, r: 2.5 }}
                activeDot={{
                  r: 5,
                  stroke: CHART_PALETTE.licenseWaste,
                  strokeWidth: 2,
                  fill: tc.chartActiveDotFill,
                }}
              />
              <Line
                yAxisId="count"
                type="monotone"
                dataKey="totalUsers"
                stroke={CHART_PALETTE.totalUsers}
                strokeWidth={1.75}
                strokeDasharray="6 4"
                name="Total Users"
                dot={{ fill: CHART_PALETTE.totalUsers, strokeWidth: 0, r: 2.5 }}
                activeDot={{
                  r: 5,
                  stroke: CHART_PALETTE.totalUsers,
                  strokeWidth: 2,
                  fill: tc.chartActiveDotFill,
                }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* ------------------------------------------------------------------ */}
      {/* Editorial summary cards                                             */}
      {/* ------------------------------------------------------------------ */}
      {trends?.trendSummary && (
        <div className="mt-7 grid grid-cols-2 gap-3 lg:grid-cols-4">
          <TrendSummaryCard
            label="MFA adoption"
            metric={trends.trendSummary.mfaAdoption}
            isPercentage={true}
          />
          <TrendSummaryCard
            label="Phish-resistant"
            metric={trends.trendSummary.phishResistantMfa}
            isPercentage={true}
          />
          <TrendSummaryCard
            label="License waste"
            metric={trends.trendSummary.licenseWaste}
            isPercentage={false}
            invertColors={true}
          />
          <TrendSummaryCard
            label="Total users"
            metric={trends.trendSummary.totalUsers}
            isPercentage={false}
          />
        </div>
      )}
    </section>
  )
}
